import ConfirmDialog from '@/components/ConfirmDialog';
import Loader from '@/components/Loader';
import Constants from '@/Constants';
import { Classification } from '@/models/Classification';
import { CurrentLearningMapRequest } from '@/models/CurrentLearningMapRequest';
import { GetClassificationsData } from '@/models/GetClassificationsData';
import { analyticsService, FlowEvents } from '@/services/AnalyticsService';
import { ApplicationState } from '@/store';
import { getClassificationsRequest } from '@/store/classification/action';
import { getCurrentLearningMapRequest, saveCurrentLearningMapRequest } from '@/store/learningMap/action';
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'underscore';
import { LearningMapRequestData } from '../../../../models/LearningMapRequestData';
import './style.scss';

interface FieldData {
    label: string;
    id: number | string | boolean;
    iconConfig?: {
        icon: string;
    };
    isSelected?: boolean;
}

interface LearninMapModalProps {
    showLearningMap: boolean;
    handleModalClose: () => void;
}
const LearninMapModal: React.FC<LearninMapModalProps> = ({ showLearningMap, handleModalClose }: LearninMapModalProps) => {
    const { classificationsList, personId, currentLearningMap, loadingLearningMap, strings, organizationId, loadingClassification } = useSelector(
        (state: ApplicationState) => ({
            classificationsList: state.classifications.data.classificationsList,
            loadingClassification: state.classifications.loading,
            personId: state.identity.data.person.id,
            currentLearningMap: state.learningMap.data.currentLearningMap,
            loadingLearningMap: state.learningMap.loading,
            strings: state.locale.data,
            organizationId: state.identity.data.user && state.identity.data.user.orgId,
        }),
    );

    const CONCEPTUAL: number = 1;
    const PRACTICAL: number = 2;
    const BASIC: number = 5;
    const SPECIALIZED: number = 6;
    const INITIAL_AVALIABLE_TIME = [
        { label: '30min', id: 30 },
        { label: '1h', id: 60 },
        { label: '2h', id: 120 },
        { label: '4h', id: 240 },
    ];
    const INITIAL_TYPE_OPTIONS = [
        { label: strings.DOCUMENT_TYPE_NAME, id: 'HTML' },
        { label: strings.VIDEO_TYPE_NAME, id: 'VIDEO' },
        { label: strings.AUDIO_TYPE_NAME, id: 'AUDIO' },
    ];
    const INTIAL_TIME_OF_DAY = [
        { label: strings.LEARNING_MAP.MORNING, id: Constants.periodOfDay.MORNING, iconConfig: { icon: Constants.icons.SUNRISE } },
        { label: strings.LEARNING_MAP.AFTERNOON, id: Constants.periodOfDay.AFTERNOON, iconConfig: { icon: Constants.icons.SUN } },
        { label: strings.LEARNING_MAP.EVENTING, id: Constants.periodOfDay.EVENING, iconConfig: { icon: Constants.icons.MOON } },
    ];
    const conceptualPratical: number[] = [CONCEPTUAL, PRACTICAL];
    const [saved, setSaved] = useState<boolean>(false);
    const basicEspecialized: number[] = [BASIC, SPECIALIZED];
    const [conceptualPraticalOptions, setConceptualPraticalOptions] = useState<Classification[]>(
        [...classificationsList].filter((item: Classification) => conceptualPratical.find((c: number) => c === item.id)),
    );
    const [basicEspecializedOptions, setBasicEspecializedOptions] = useState<Classification[]>(
        [...classificationsList].filter((item: Classification) => basicEspecialized.find((c: number) => c === item.id)),
    );

    const [emailSuggestions, setEmailSuggestions] = useState<boolean | undefined>(true);
    const [learningMapData, setLearningMapData] = useState<LearningMapRequestData>();
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const [showClose, setShowClose] = useState(false);

    const [availableTimeOptions, setAvailableTimeOptions] = useState<FieldData[]>(INITIAL_AVALIABLE_TIME);
    const [mediaTypeOptions, setMediaTypeOptions] = useState<FieldData[]>(INITIAL_TYPE_OPTIONS);
    const [timeOfTheDay, setTimeOfTheDay] = useState<FieldData[]>(INTIAL_TIME_OF_DAY);

    const dispatch = useDispatch();

    useEffect(() => {
        resetForm();
        setCurrentLearningMap();
    }, [showLearningMap, currentLearningMap]);

    useEffect(() => {
        return () => {
            classificationsList.map((item: Classification) => {
                item.isSelected = false;
                return item;
            });
        };
    }, []);

    useEffect(() => {
        const requestData: LearningMapRequestData = getRequestData();

        setIsSaveDisabled(disableSaveButton(requestData));
        setLearningMapData(requestData);
    }, [availableTimeOptions, mediaTypeOptions, timeOfTheDay, conceptualPraticalOptions, basicEspecializedOptions, emailSuggestions, isSaveDisabled]);

    const resetForm = () => {
        setSaved(false);
        setEmailSuggestions(true);
        setIsSaveDisabled(true);
        setAvailableTimeOptions(INITIAL_AVALIABLE_TIME);
        setMediaTypeOptions(INITIAL_TYPE_OPTIONS);
        setTimeOfTheDay(INTIAL_TIME_OF_DAY);
        setConceptualPraticalOptions(
            [...classificationsList].filter((item: Classification) => conceptualPratical.find((c: number) => c === item.id)),
        );
        setBasicEspecializedOptions([...classificationsList].filter((item: Classification) => basicEspecialized.find((c: number) => c === item.id)));
    };

    const setCurrentLearningMap = () => {
        if (currentLearningMap) {
            currentLearningMap.favoriteContentType.forEach((item: string) => handleOnPressOption('favoriteContentType', item));
            currentLearningMap.classificationIds
                .filter((item: number) => conceptualPratical.find((c: number) => c === item))
                .forEach((item: number) => {
                    handleOnPressOption('conceptualPratical', item);
                });
            currentLearningMap.classificationIds
                .filter((item: number) => basicEspecialized.find((c: number) => c === item))
                .forEach((item: number) => {
                    handleOnPressOption('basicEspecialized', item);
                });
            handleOnPressOption('minutes', Number(currentLearningMap.minutes));
            handleOnPressOption('suggestionTimeOfTheDay', currentLearningMap.suggestionTimeOfTheDay as string);
            setEmailSuggestions(currentLearningMap.emailSuggestions);
        }
    };

    const handleOnPressSave = () => {
        if (learningMapData) {
            dispatch(saveCurrentLearningMapRequest(learningMapData));
            analyticsService.logEvent(FlowEvents.SAVE_LEARNING_MAP_MY_PROFILE);
            setSaved(true);
        }
    };

    const makeInitialRequests = () => {
        if (strings._LANGUAGE === 'pt') {
            if (classificationsList.length === 0)
                dispatch(
                    getClassificationsRequest({
                        organizationId,
                        applicationId: Constants.APP_ID,
                    } as GetClassificationsData),
                );
            dispatch(
                getCurrentLearningMapRequest({
                    withTotalFinishedContents: true,
                    withMediaTypeConsumption: true,
                } as CurrentLearningMapRequest),
            );
        }
    };

    useEffect(() => {
        resetForm();
        setCurrentLearningMap();
    }, [loadingClassification, loadingLearningMap]);

    const hasLearningMapChanged = () => {
        if (learningMapData && !currentLearningMap) {
            return !!(
                !Number.isNaN(learningMapData.minutes || 0) ||
                learningMapData.suggestionTimeOfTheDay ||
                learningMapData.emailSuggestions === false ||
                (learningMapData.favoriteContentType && learningMapData.favoriteContentType.length !== 0) ||
                (learningMapData.classificationIds && learningMapData.classificationIds.length !== 0)
            );
        }
        if (currentLearningMap && learningMapData) {
            const favoriteContentType: string[] = learningMapData.favoriteContentType ? learningMapData.favoriteContentType.sort() : [];
            const classificationIds: number[] = learningMapData.classificationIds ? learningMapData.classificationIds.sort() : [];
            const timeOfTheDaySelected: FieldData | undefined = timeOfTheDay.find((item: FieldData) => item.isSelected);

            return (
                !_.isEqual(currentLearningMap.favoriteContentType.sort(), favoriteContentType) ||
                !_.isEqual(currentLearningMap.classificationIds.sort(), classificationIds) ||
                currentLearningMap.minutes !== learningMapData.minutes ||
                currentLearningMap.emailSuggestions !== learningMapData.emailSuggestions ||
                currentLearningMap.suggestionTimeOfTheDay !== learningMapData.suggestionTimeOfTheDay ||
                !timeOfTheDaySelected
            );
        }
    };

    const checkSelected = (item: FieldData | Classification, value: number | string | boolean, multiSelect: boolean): any => {
        if (item.id === value && !item.isSelected) {
            item.isSelected = true;
        } else if (multiSelect && item.id === value) {
            item.isSelected = false;
        } else if (!multiSelect) {
            item.isSelected = false;
        }
        return item;
    };

    const handleOnPressOption = (field: string, value: number | string | boolean) => {
        switch (field) {
            case 'minutes':
                setAvailableTimeOptions(availableTimeOptions.map((item: FieldData) => checkSelected(item, value, false)));
                break;
            case 'favoriteContentType':
                setMediaTypeOptions(mediaTypeOptions.map((item: FieldData) => checkSelected(item, value, true)));
                break;
            case 'suggestionTimeOfTheDay':
                setTimeOfTheDay(timeOfTheDay.map((item: FieldData) => checkSelected(item, value, false)));
                break;
            case 'basicEspecialized':
                setBasicEspecializedOptions(basicEspecializedOptions.map((item: Classification) => checkSelected(item, value, false)));
                break;
            case 'conceptualPratical':
                setConceptualPraticalOptions(conceptualPraticalOptions.map((item: Classification) => checkSelected(item, value, false)));
                break;
            default:
                break;
        }
    };

    const retriveIcon = (id: string) => {
        if (id === 'MORNING') return <Icon.Sunrise />;
        if (id === 'AFTERNOON') return <Icon.Sun />;
        if (id === 'EVENING') return <Icon.Moon />;
    };

    const confirmCloseModal = (close: boolean) => {
        if (close) {
            makeInitialRequests();
            handleModalClose();
        }
        setShowClose(false);
    };

    const showCloseModal = () => {
        if (hasLearningMapChanged()) setShowClose(true);
        else handleModalClose();
    };

    const showSave = () => {
        return isSaveDisabled || loadingLearningMap || !hasLearningMapChanged();
    };

    const getRequestData = (): LearningMapRequestData => {
        const availableTimeOptionsSelected: FieldData | undefined = availableTimeOptions.find((item: FieldData) => item.isSelected);
        const timeOfTheDaySelected: FieldData | undefined = timeOfTheDay.find((item: FieldData) => item.isSelected);

        const favoriteContentType: string[] = mediaTypeOptions
            .filter((item: FieldData) => item.isSelected)
            .map((item: FieldData) => item.id) as string[];

        const classifications: Classification[] = [...conceptualPraticalOptions, ...basicEspecializedOptions];
        const classificationIds: number[] = classifications
            .filter((item: Classification) => item.isSelected)
            .map((item: Classification) => item.id) as number[];

        const suggestionTimeOfTheDay = timeOfTheDaySelected
            ? timeOfTheDaySelected.id.toString()
            : emailSuggestions
            ? ''
            : Constants.periodOfDay.MORNING;

        return {
            minutes: Number(availableTimeOptionsSelected ? availableTimeOptionsSelected.id : undefined),
            favoriteContentType,
            classificationIds,
            emailSuggestions,
            suggestionTimeOfTheDay,
            personId,
            id: currentLearningMap?.id || 0,
        } as LearningMapRequestData;
    };

    const disableSaveButton = (requestData: LearningMapRequestData): boolean => {
        const disable = !!Object.keys(requestData).some((key: string) => {
            const fieldValue = (requestData as any)[key];
            const isKeyId: boolean = key === 'id';
            const disableField: boolean = fieldValue === undefined || fieldValue === '' || Number.isNaN(fieldValue);
            const disableClassifications: boolean = key === 'classificationIds' && fieldValue.length === 0;
            const disableFavoriteContentType: boolean = key === 'favoriteContentType' && fieldValue.length === 0;

            return !isKeyId && (disableField || disableClassifications || disableFavoriteContentType);
        });

        return disable;
    };

    return (
        <>
            <Modal
                centered
                id="change-modal"
                onHide={() => {
                    showCloseModal();
                }}
                show={showLearningMap && (!loadingLearningMap || saved)}
            >
                {
                    <div className={`learning-map ${showClose && ' hide'}`}>
                        <div className="change-modal-content modal-content ">
                            <div className="modal-title title-learning-map">
                                <div className="text-title">
                                    <h1> {strings.ONBOARDING.LEARNING_MAP}</h1>
                                </div>
                                <i
                                    className="material-icons-outlined align-middle close-icon"
                                    data-dismiss="modal"
                                    onClick={() => {
                                        showCloseModal();
                                    }}
                                    onKeyDown={() => {}}
                                    role="button"
                                    tabIndex={0}
                                >
                                    {Constants.icons.CLOSE}
                                </i>
                            </div>
                            {currentLearningMap && (
                                <div className="input-section">
                                    <div className="alert-container">
                                        <i className="material-icons align-middle icon-alert">{Constants.icons.ERROR_OUTLINE}</i>
                                        <span>{strings.LEARNING_MAP.LEARNING_MAP_EDIT_OVERWRITE_MESSAGE}</span>
                                    </div>
                                </div>
                            )}
                            <div className="input-section">
                                <div className="title-container">
                                    <h1>{strings.LEARNING_MAP.WEEKLY_TIME_AVAILABLE}</h1>
                                </div>
                                <div className="buttons-learning-map">
                                    {availableTimeOptions.map((item: FieldData) => (
                                        <div className={`btn-learning-map`} key={item.id + ''}>
                                            <button
                                                className={`btn btn-primary btn-sm  ${item.isSelected && ' is-active'}`}
                                                onClick={() => handleOnPressOption('minutes', item.id)}
                                                type="button"
                                            >
                                                {item.label}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="input-section">
                                <div className="title-container">
                                    <h1>{strings.LEARNING_MAP.FAVORITE_MEDIA}</h1>
                                </div>
                                <div className="buttons-learning-map">
                                    {mediaTypeOptions.map((item: FieldData) => (
                                        <div className={`btn-learning-map`} key={item.id + ''}>
                                            <button
                                                className={`btn btn-primary btn-sm  ${item.isSelected && ' is-active'}`}
                                                onClick={() => handleOnPressOption('favoriteContentType', item.id)}
                                                type="button"
                                            >
                                                {item.label}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="input-section">
                                <div className="title-container">
                                    <h1>{strings.LEARNING_MAP.LEARNING_ITEMS_CHARACTERISTICS}</h1>
                                </div>
                                <div className="characteristics-buttons-container">
                                    <div className="characteristics-row">
                                        {basicEspecializedOptions.map((item: Classification) => (
                                            <div className={`btn-learning-map`} key={item.id + ''}>
                                                <button
                                                    className={`btn btn-primary btn-sm   ${item.isSelected && ' is-active'}`}
                                                    onClick={() => handleOnPressOption('basicEspecialized', item.id)}
                                                    type="button"
                                                >
                                                    {item.name}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="characteristics-row">
                                        {conceptualPraticalOptions.map((item: Classification) => (
                                            <div className={`btn-learning-map`} key={item.id + ''}>
                                                <button
                                                    className={`btn btn-primary btn-sm ${item.isSelected && ' is-active'}`}
                                                    onClick={() => handleOnPressOption('conceptualPratical', item.id)}
                                                    type="button"
                                                >
                                                    {item.name}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="input-section">
                                <div className="title-container">
                                    <h1>{strings.LEARNING_MAP.GET_SUGGESTIONS_BY_EMAIL}</h1>
                                </div>
                                <div className="buttons-learning-map">
                                    <div className="btn-learning-map">
                                        <button
                                            className={`btn btn-primary btn-sm  btn-email-left ${emailSuggestions && ' is-active'} `}
                                            onClick={() => setEmailSuggestions(true)}
                                            type="button"
                                        >
                                            {strings.YES}
                                        </button>
                                        <button
                                            className={`btn btn-primary btn-sm  btn-email-right ${!emailSuggestions && ' is-active'} `}
                                            onClick={() => setEmailSuggestions(false)}
                                            type="button"
                                        >
                                            {strings.NO}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="input-section">
                                <div className="title-container">
                                    <h1>{strings.LEARNING_MAP.TIME_OF_THE_DAY_TO_GET_SUGGESTIONS}</h1>
                                </div>
                                <div className="buttons-learning-map">
                                    {timeOfTheDay.map((item: FieldData) => (
                                        <div className={`btn-learning-map`} key={item.id + ''}>
                                            <button
                                                className={`btn btn-primary btn-sm btn-time-of-day  ${item.isSelected && ' is-active'}`}
                                                onClick={() => handleOnPressOption('suggestionTimeOfTheDay', item.id)}
                                                type="button"
                                            >
                                                <span className="spn-time-of-day">{retriveIcon(item.id + '')}</span>
                                                {item.label}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-confirm-button">
                                <button className="btn btn-secondary confirm-button" disabled={showSave()} onClick={handleOnPressSave} type="button">
                                    {strings.SAVE}
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </Modal>
            {loadingLearningMap && <Loader />}
            <ConfirmDialog
                content={strings.LEAVE_SECTION_MESSAGE}
                leftButtonTitle={strings.CONFIRM_EXIT}
                leftClick={() => confirmCloseModal(true)}
                rightClick={() => confirmCloseModal(false)}
                show={showClose}
            />
        </>
    );
};

export default LearninMapModal;
